import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { browser } from '../../global/browser';
import { CMS_CONTENT_MODES } from '../../cms';
import styles from './ui-columns.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIColumns
 * @element ui-columns
 * @classdesc Represents a class for <code>ui-columns</code> element.
 * Represent columns layout, should contain only <b>&lt;ui-column></b> children elements.
 * @property {boolean} [wrap=false] {@attr wrap} Columns behave like flex-wrap CSS property.
 * 5 columns per row in desktop, 3 in tablet, 1 in mobile.
 * @property {string} [colwidth] {@attr colwidth} Fixed width for column (not mandatory).
 * @property {("left" | "center" | "right" | "space-between" | "space-around")} [halign]
 *  {@attr halign} Horizontal alignment like flex.
 *  {@desc left}
 *  {@desc center}
 *  {@desc right}
 *  {@desc space-between}
 *  {@desc space-around}
 * @property {("top" | "bottom" | "center" | "stretch")} [valign] {@attr valign}
 *  Vertical alignment like flex.
 *  {@desc left}
 *  {@desc center}
 *  {@desc right}
 *  {@desc stretch}
 * @property {boolean} [inrow=false] {@attr inrow} If set to true then on small screens the columns will
 * shrink and stay in a row instead of wrapping into one column.
 * @property {boolean} [bordered=false] {@attr bordered} Draw right border for each column except
 * last child.
 * @slot {@type "ui-column"}
 * @example
 * <ui-columns>
 *   <ui-column>I'm first column</ui-column>
 *   <ui-column>I'm second column</ui-column>
 *   <ui-column>I'm third column</ui-column>
 * </ui-columns>
 */
@Component({
    tag: 'ui-columns',
    styles,
    cms: {
        mode: CMS_CONTENT_MODES.Children,
        allowedChildren: ['ui-column'],
    },
})
export class UIColumns extends UIElement {
    private readonly mutationObserver = new MutationObserver(
        this.handleControlMutations.bind(this)
    );

    private readonly appearanceChangeHandler =
        this.handleChangeAppearance.bind(this);

    @Attribute(Boolean) wrap?: boolean;
    @Attribute(String) colwidth?: string;
    @Attribute(String) halign?: string;
    @Attribute(String) valign?: string;
    @Attribute(Boolean) inrow?: boolean;
    @Attribute(Boolean) bordered?: boolean;

    applyColumnWidth(): void {
        if (this.colwidth) {
            Array.from(this.children).forEach((node) => {
                (node as HTMLElement).style.flexBasis = this.colwidth!;
                (node as HTMLElement).style.maxWidth = this.colwidth!;
            });
        }
    }

    /**
     * Update columns appearance with colwidth.
     */
    updateColumnsAppearance() {
        if (browser.mobileMediaMatches() && !this.inrow) {
            Array.from(this.children).forEach((node) => {
                (node as HTMLElement).removeAttribute('style');
            });
        } else {
            this.applyColumnWidth();
        }
    }

    /**
     * Handle event when media changed desktop to mobile and vice versa.
     * @private
     */
    private handleChangeAppearance(): void {
        this.updateColumnsAppearance();
    }

    /**
     * @inheritDoc
     */
    handleControlMutations(): void {
        this.updateColumnsAppearance();
    }

    /**
     * @private
     */
    private subscribeEvents(): void {
        browser.mobileMedia.addEventListener(
            'change',
            this.appearanceChangeHandler
        );
        this.mutationObserver.observe(this, {
            childList: true,
        });
    }

    /**
     * @private
     */
    private unsubscribeEvents(): void {
        browser.mobileMedia.removeEventListener(
            'change',
            this.appearanceChangeHandler
        );
        this.mutationObserver.disconnect();
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        if (!browser.mobileMediaMatches()) {
            this.applyColumnWidth();
        }
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.handleChangeAppearance();
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        this.subscribeEvents();
    }
}
